// ContactKommunikationView.tsx — Kontakt-Reiter „Kommunikation".
//
// Redesign 02.11.2026 (kvp-rd, User: „tabellarisch, sortierbar, durchsuchbar,
// Posteingang und Archiv nicht mehr trennen, Laden dauert zu lange"):
//   - EINE Werkzeugleiste: Suche (über Betreff, Absender, Empfänger, Vorschau
//     der Mails und über Name/Nummer der Anrufe) + Aktualisieren.
//   - MAILS: ein gemeinsamer Bestand als Tabelle (ContactMailView).
//   - ANRUFE darunter als Tabelle — Richtung, Datum/Uhrzeit, Gegenstelle,
//     Dauer, Status; nicht erreichte offene Anrufe hervorgehoben, „erledigt"
//     und „Rückmeldung senden" bleiben (AP10/AP11). Quelle:
//     telephonyService.listCalls (serverseitig paginiert, „Mehr laden").
//
// Leere Sektionen werden NICHT gerendert (A2); ist ALLES leer, erscheint EIN
// gemeinsamer Leerzustand. Ein Ladefehler hält die jeweilige Sektion sichtbar.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spinner } from "@fluentui/react-components";
import { Checkmark16Regular, MailArrowUp16Regular } from "@fluentui/react-icons";
import { I } from "./redesign/kvpIcons";
import { DataTable, type Column, type DateRangeValue } from "./redesign/table";
import {
    ANRUF_RICHTUNGEN,
    ANRUF_STATUS,
    directionLabel,
    filtereAnrufSpalten,
    isMissed,
    startZeit,
} from "./kommunikationAnrufe";
import { ContactMailView } from "./ContactMailView";
import { telephonyService, CallLogDto } from "../services/telephonyService";
import { useResponsive } from "../hooks/useResponsive";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { openComposeWindow } from "./mail/ComposeMailWindow";
import { NICHT_ERREICHT_MAIL } from "./telephony/callFollowUpMail";
import { KOMM_PAGE_SIZE, hasMoreServer, sectionVisibility } from "./kommunikationHelpers";
import { formatZeit, passtZurSuche, suchWoerter, type KommSort } from "./kommunikationTabelle";
import { alsWeitererKontakt } from "./telephony/callKontakte";
import "./redesign/contactRedesign.css";
import "./kommunikation.css";

// ── AP11: „nicht erreicht"-Mailvorlage (USP, AC3) ──────────────────────────
//
// Wird ein Anruf nicht erreicht (siehe isMissed), bietet die Anruf-Tabelle die
// Aktion „Rückmeldung senden" an — ABER nur, wenn der Kontakt eine E-Mail-
// Adresse hat (AC5). Klick öffnet das bestehende Compose-Fenster vorbefüllt
// (Empfänger = Kontakt-Mail, Betreff + Body = Vorlage). Versand erfolgt dort
// BEWUSST über den bestehenden Sende-Weg — KEIN Auto-Versand. Der zentrale
// Vorlagentext lebt in `telephony/callFollowUpMail.ts`.

interface Props {
    contactId: number;
    isCompany?: boolean;
    /** Email address(es) of the contact itself */
    emailAddresses: string[];
}

// ── Helfer: Anruf-Darstellung ──────────────────────────────────────────────


function formatDuration(sec?: number | null): string {
    const s = sec ?? 0;
    if (s <= 0) return "—";
    const m = Math.floor(s / 60);
    const r = s % 60;
    return m > 0 ? `${m}:${String(r).padStart(2, "0")} min` : `${r}s`;
}

/** Anzeige-Nummer je nach Richtung (Gegenstelle). */
function counterpartNumber(call: CallLogDto): string {
    const num = call.direction === "outbound" ? call.toNumber : call.fromNumber;
    return num || "Unbekannte Nummer";
}

/** Suche über die geladenen Anrufe: Name, Nummern, Richtung, Status. */
export function anrufPasst(call: CallLogDto, suche: string): boolean {
    const woerter = suchWoerter(suche);
    if (woerter.length === 0) return true;
    const text = [
        call.contactDisplayName,
        call.fromNumber,
        call.toNumber,
        call.userName,
        call.status,
        directionLabel(call.direction),
        isMissed(call) ? "nicht erreicht" : "",
        call.notes,
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    return passtZurSuche(text, woerter);
}

/** Sortierung der GELADENEN Anrufe; ohne Sortierung neueste zuerst. */
export function sortiereAnrufe(calls: CallLogDto[], sort: KommSort): CallLogDto[] {
    const feld = sort?.field ?? "datum";
    const dir = sort?.direction === "asc" ? 1 : -1;
    const wert = (c: CallLogDto): number | string => {
        if (feld === "dauer") return c.talkDurationSec ?? 0;
        if (feld === "richtung") return directionLabel(c.direction);
        if (feld === "gegenstelle") return c.contactDisplayName || counterpartNumber(c);
        if (feld === "status") return isMissed(c) ? "0" : c.status ?? "";
        return startZeit(c);
    };
    return calls
        .map((c, i) => ({ c, i }))
        .sort((a, b) => {
            const va = wert(a.c);
            const vb = wert(b.c);
            const cmp =
                typeof va === "number" && typeof vb === "number"
                    ? va - vb
                    : String(va).localeCompare(String(vb), "de", { sensitivity: "base" });
            return cmp * dir || startZeit(b.c) - startZeit(a.c) || a.i - b.i;
        })
        .map(({ c }) => c);
}

// ── Abschnittskopf (Klappknopf + Zähler + Aktionen) ─────────────────────────

const AbschnittKopf: React.FC<{
    icon: React.ReactNode;
    titel: string;
    anzahl?: number;
    zu: boolean;
    onToggle: () => void;
    extra?: React.ReactNode;
}> = ({ icon, titel, anzahl, zu, onToggle, extra }) => (
    <div className="kkm-kopf">
        <button type="button" className={"kkm-kopf-knopf" + (zu ? " zu" : "")} onClick={onToggle} aria-expanded={!zu}>
            <span className="kkm-chev" aria-hidden>
                {I.chevD}
            </span>
            <span className="kkm-icon" aria-hidden>
                {icon}
            </span>
            {titel}
        </button>
        {anzahl !== undefined && <span className="kkm-zahl">{anzahl}</span>}
        {extra}
    </div>
);

// ── Hauptkomponente ─────────────────────────────────────────────────────────

export const ContactKommunikationView: React.FC<Props> = ({ contactId, isCompany, emailAddresses }) => {
    const { isPhone } = useResponsive();
    const { openWindow } = useWindowManager();

    const [suche, setSuche] = useState("");
    const [aktualisieren, setAktualisieren] = useState(0);

    // Anrufe (serverseitig paginiert: limit/offset/total).
    const [calls, setCalls] = useState<CallLogDto[]>([]);
    const [callsTotal, setCallsTotal] = useState(0);
    const [callsLoading, setCallsLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [callsError, setCallsError] = useState<string | null>(null);
    const [resolvingId, setResolvingId] = useState<number | null>(null);
    const [callSort, setCallSort] = useState<KommSort>({ field: "datum", direction: "desc" });
    const [callFilter, setCallFilter] = useState<Record<string, string | boolean | undefined>>({});
    const [callDatum, setCallDatum] = useState<DateRangeValue>({});
    useEffect(() => {
        setCallFilter({});
        setCallDatum({});
    }, [contactId]);
    const callFilterAktiv = Object.values(callFilter).some((v) => v !== undefined && v !== "") || !!(callDatum.von || callDatum.bis);

    // Mails: Zähler + Fehlerzustand kommen aus ContactMailView (Zähler im Kopf,
    // „leere Sektion ausblenden"; ein Fehler hält die Sektion sichtbar).
    const [mailCount, setMailCount] = useState(0);
    const [mailLoaded, setMailLoaded] = useState(false);
    const [mailError, setMailError] = useState(false);

    const [mailsCollapsed, setMailsCollapsed] = useState(false);
    const [callsCollapsed, setCallsCollapsed] = useState(false);

    // Kontaktwechsel: Mail-/Einklapp-/Suchzustand zurücksetzen.
    useEffect(() => {
        setMailCount(0);
        setMailLoaded(false);
        setMailError(false);
        setMailsCollapsed(false);
        setCallsCollapsed(false);
        setSuche("");
    }, [contactId]);

    const loadInitial = useCallback(async () => {
        if (!contactId) {
            setCalls([]);
            setCallsTotal(0);
            setCallsLoading(false);
            return;
        }
        setCallsLoading(true);
        setCallsError(null);
        try {
            const res = await telephonyService.listCalls({ contactId, limit: KOMM_PAGE_SIZE, offset: 0 });
            setCalls(res.calls);
            setCallsTotal(res.total);
        } catch (err: any) {
            setCallsError(err?.message ?? "Fehler beim Laden der Anrufe");
        } finally {
            setCallsLoading(false);
        }
    }, [contactId]);

    const loadMoreCalls = useCallback(async () => {
        if (loadingMore) return;
        setLoadingMore(true);
        try {
            const res = await telephonyService.listCalls({
                contactId,
                limit: KOMM_PAGE_SIZE,
                offset: calls.length,
            });
            setCalls((prev) => [...prev, ...res.calls]);
            setCallsTotal(res.total);
        } catch (err: any) {
            setCallsError(err?.message ?? "Fehler beim Nachladen der Anrufe");
        } finally {
            setLoadingMore(false);
        }
    }, [contactId, calls.length, loadingMore]);

    useEffect(() => {
        void loadInitial();
    }, [loadInitial]);

    // AP10: offenen Anruf erledigen (resolvedAt); der Server liefert den finalen Datensatz.
    const handleResolve = useCallback(async (id: number) => {
        setResolvingId(id);
        try {
            const updated = await telephonyService.resolveCall(id);
            setCalls((prev) => prev.map((c) => (c.id === id ? updated : c)));
        } catch (err: any) {
            setCallsError(err?.message ?? "Anruf konnte nicht erledigt werden");
        } finally {
            setResolvingId(null);
        }
    }, []);

    // AP11: erste verwertbare E-Mail-Adresse des Kontakts (AC5: ohne Adresse kein Angebot).
    const contactMail = (emailAddresses ?? []).map((e) => (e ?? "").trim()).find((e) => e.length > 0) ?? null;
    const canSendMail = !!contactMail;

    const handleSendCallbackMail = useCallback(
        (call: CallLogDto) => {
            if (!contactMail) return;
            openComposeWindow(openWindow, {
                to: [contactMail],
                subject: NICHT_ERREICHT_MAIL.subject,
                // T-41: Die Büroklammer startet mit den Dokumenten dieses Kontakts.
                contactId,
                // A2: Firma → „Hallo zusammen,"; Person → nur mit sicherem Vornamen.
                body: NICHT_ERREICHT_MAIL.body({
                    isCompany: call.contactIsCompany ?? isCompany,
                    firstName: call.contactFirstName,
                }),
            });
        },
        [contactMail, openWindow, isCompany, contactId],
    );

    const handleMailCount = useCallback((n: number) => {
        setMailCount(n);
        setMailLoaded(true);
    }, []);

    const missedCount = calls.filter((c) => isMissed(c) && !c.resolvedAt).length;
    const vis = sectionVisibility({
        mailLoaded,
        mailCount,
        mailError,
        callsLoading,
        callsError: !!callsError,
        callsTotal,
    });

    const sichtbareCalls = useMemo(
        () =>
            sortiereAnrufe(
                filtereAnrufSpalten(calls.filter((c) => anrufPasst(c, suche)), callFilter, callDatum),
                callSort,
            ),
        [calls, suche, callSort, callFilter, callDatum],
    );

    const callColumns: Column<CallLogDto>[] = useMemo(
        () => [
            {
                id: "datum",
                header: "Datum",
                sortable: true,
                width: 140,
                filter: { kind: "dateRange", rueckblick: true },
                cell: (c) => {
                    const { datum, uhrzeit } = formatZeit(startZeit(c));
                    return (
                        <span className="kkm-datum">
                            {datum}
                            {uhrzeit && <small>{uhrzeit}</small>}
                        </span>
                    );
                },
            },
            {
                id: "richtung",
                header: "Richtung",
                sortable: true,
                width: 156,
                filter: { kind: "dropdown", options: [...ANRUF_RICHTUNGEN] },
                cell: (c) => {
                    const missed = isMissed(c);
                    const klasse = missed ? "verpasst" : c.direction === "outbound" ? "ausgang" : c.direction === "internal" ? "intern" : "eingang";
                    return (
                        <span className={`kkm-richtung ${klasse}`}>
                            <span style={{ display: "inline-flex", transform: c.direction === "outbound" ? undefined : "scaleX(-1)" }}>
                                {c.direction === "outbound" ? I.arrowR : I.phone}
                            </span>
                            {directionLabel(c.direction)}
                        </span>
                    );
                },
            },
            {
                id: "gegenstelle",
                header: "Gegenstelle",
                sortable: true,
                minWidth: 240,
                filter: { kind: "text" },
                className: "kt-wrap",
                cell: (c) => (
                    <span>
                        {c.contactDisplayName || counterpartNumber(c)}
                        {/* Ein Anruf kann mehreren Kontakten gehören. Hängt dieser
                            Kontakt nur als WEITERER daran, steht hier der Name des
                            Hauptkontakts. */}
                        {alsWeitererKontakt(c, contactId) && <span className="kkm-leer"> (als weiterer Kontakt)</span>}
                        {c.contactDisplayName && <span className="kkm-vorschau" style={{ display: "block" }}>{counterpartNumber(c)}</span>}
                    </span>
                ),
            },
            {
                id: "dauer",
                header: "Dauer",
                sortable: true,
                width: 104,
                cell: (c) => formatDuration(c.talkDurationSec),
            },
            {
                id: "status",
                header: "Status",
                sortable: true,
                width: 150,
                filter: { kind: "dropdown", options: [...ANRUF_STATUS] },
                cell: (c) =>
                    c.resolvedAt ? (
                        <span className="kkm-erledigt-pill">Erledigt</span>
                    ) : isMissed(c) ? (
                        <span className="kkm-richtung verpasst">Nicht erreicht</span>
                    ) : c.status ? (
                        <span className="kkm-richtung intern">{c.status}</span>
                    ) : null,
            },
            {
                id: "aktionen",
                header: "",
                pinned: true,
                minWidth: 240,
                align: "right",
                cell: (c) => {
                    const missed = isMissed(c);
                    const resolved = !!c.resolvedAt;
                    if (resolved) return null;
                    return (
                        <span className="kkm-aktionen" onClick={(e) => e.stopPropagation()}>
                            {/* AP11: nicht erreicht + Kontakt-Mail vorhanden → Vorlage öffnen. */}
                            {missed && canSendMail && (
                                <button
                                    type="button"
                                    className="tbtn"
                                    onClick={() => handleSendCallbackMail(c)}
                                    title="Rückmeldung per E-Mail senden (Vorlage öffnen)"
                                >
                                    <MailArrowUp16Regular />
                                    Rückmeldung senden
                                </button>
                            )}
                            {/* AP10: offen → „erledigt" */}
                            <button
                                type="button"
                                className="tbtn"
                                disabled={resolvingId === c.id}
                                onClick={() => void handleResolve(c.id)}
                                title="Anruf als erledigt markieren"
                            >
                                <Checkmark16Regular />
                                {resolvingId === c.id ? "…" : "erledigt"}
                            </button>
                        </span>
                    );
                },
            },
        ],
        [contactId, canSendMail, handleSendCallbackMail, handleResolve, resolvingId],
    );

    return (
        <div className="kvp-rd" style={{ height: "100%", overflowY: "auto" }}>
            <div className="kkm-wrap">
                {/* ── Werkzeugleiste ───────────────────────────────────────── */}
                <div className="kkm-leiste">
                    <label className="search">
                        {I.search}
                        <input
                            type="search"
                            value={suche}
                            onChange={(e) => setSuche(e.target.value)}
                            placeholder="Betreff, Absender, Empfänger, Vorschau …"
                            aria-label="Kommunikation durchsuchen"
                        />
                        {suche && (
                            <button type="button" className="kkm-suche-leeren" onClick={() => setSuche("")} aria-label="Suche leeren" title="Suche leeren">
                                ×
                            </button>
                        )}
                    </label>
                    <span className="kkm-leiste-rechts">
                        <button
                            type="button"
                            className="tbtn"
                            onClick={() => {
                                setAktualisieren((n) => n + 1);
                                void loadInitial();
                            }}
                            title="Mails und Anrufe aktualisieren"
                            style={isPhone ? { minWidth: 44, minHeight: 44 } : undefined}
                        >
                            ↻ Aktualisieren
                        </button>
                    </span>
                </div>

                {/* ── Abschnitt 1: MAILS ───────────────────────────────────── */}
                {vis.showMails && (
                    <section className="kkm-abschnitt">
                        <AbschnittKopf
                            icon={I.mail}
                            titel="Mails"
                            anzahl={mailLoaded ? mailCount : undefined}
                            zu={mailsCollapsed}
                            onToggle={() => setMailsCollapsed((c) => !c)}
                        />
                        {/* Bleibt beim Einklappen montiert (Zähler + Ladezustand). */}
                        <div style={{ display: mailsCollapsed ? "none" : "block" }}>
                            <ContactMailView
                                contactId={contactId}
                                isCompany={isCompany}
                                emailAddresses={emailAddresses}
                                onCountChange={handleMailCount}
                                onErrorChange={setMailError}
                                suche={suche}
                                aktualisieren={aktualisieren}
                            />
                        </div>
                    </section>
                )}

                {/* ── Abschnitt 2: ANRUFE ──────────────────────────────────── */}
                {vis.showCalls && (
                    <section className="kkm-abschnitt">
                        <AbschnittKopf
                            icon={I.phone}
                            titel="Anrufe"
                            anzahl={callsLoading ? undefined : callsTotal}
                            zu={callsCollapsed}
                            onToggle={() => setCallsCollapsed((c) => !c)}
                            extra={
                                missedCount > 0 ? <span className="kkm-zahl warn">{missedCount} nicht erreicht</span> : undefined
                            }
                        />
                        {!callsCollapsed &&
                            (callsLoading ? (
                                <span className="kkm-status">
                                    <Spinner size="extra-tiny" /> Anrufe werden geladen…
                                </span>
                            ) : callsError ? (
                                <div className="kkm-hinweis fehler" role="alert">
                                    {callsError}
                                    <button type="button" className="tbtn" onClick={() => void loadInitial()}>
                                        Erneut versuchen
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <DataTable
                                        columns={callColumns}
                                        rows={sichtbareCalls}
                                        rowKey={(c) => c.id}
                                        rowClassName={(c) =>
                                            c.resolvedAt ? "kkm-erledigt" : isMissed(c) ? "kkm-verpasst" : undefined
                                        }
                                        sort={callSort}
                                        onSortChange={(next) => setCallSort(next ?? { field: "datum", direction: "desc" })}
                                        filterValues={callFilter}
                                        onFilterChange={(id, v) => setCallFilter((prev) => ({ ...prev, [id]: v }))}
                                        dateRangeValues={{ datum: callDatum }}
                                        onDateRangeChange={(_id, next) => setCallDatum(next)}
                                        cardClassName="kkm-karte"
                                        tableClassName="kt-dense kkm-tabelle kkm-tabelle-anrufe"
                                        emptyText={
                                            callFilterAktiv
                                                ? "Kein geladener Anruf passt zu den Filtern."
                                                : suche.trim()
                                                  ? "Kein geladener Anruf passt zur Suche."
                                                  : "Keine Anrufe."
                                        }
                                    />
                                    {callFilterAktiv && (
                                        <div className="kkm-hinweis kkm-filterhinweis">
                                            Gefiltert wird über die geladenen Anrufe ({sichtbareCalls.length} von {calls.length}).
                                            <button
                                                type="button"
                                                className="tbtn"
                                                onClick={() => {
                                                    setCallFilter({});
                                                    setCallDatum({});
                                                }}
                                            >
                                                Filter zurücksetzen
                                            </button>
                                        </div>
                                    )}
                                    {hasMoreServer(calls.length, callsTotal) && (
                                        <div className="kkm-mehr">
                                            <button type="button" className="tbtn" disabled={loadingMore} onClick={() => void loadMoreCalls()}>
                                                {loadingMore ? "Lädt…" : `Mehr laden (${callsTotal - calls.length} weitere)`}
                                            </button>
                                        </div>
                                    )}
                                </>
                            ))}
                    </section>
                )}

                {/* ── Gemeinsamer Leerzustand: weder Mails noch Anrufe ──────── */}
                {vis.allEmpty && (
                    <div className="kkm-leer-gesamt">
                        <div className="kkm-leer-icons">
                            {I.mail}
                            {I.phone}
                        </div>
                        Keine Kommunikation zu diesem Kontakt.
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContactKommunikationView;
